/**
 * DetailRow — one compact label/value line for the site context cards.
 *
 * The building block for the Structure and Device detail lists in the
 * dashboard rail: a small muted label with the value beneath it, an
 * optional trailing action (map link, copy button, signal icon), and no
 * divider — rows are separated by spacing, not rules, so a stack of them
 * reads as a tight modern card instead of a heavy ledger.
 *
 * Contains:
 * - DetailRow — the label/value line (default export)
 */
import { Box, Typography } from '@mui/material';
import React from 'react';

/** The shared caption-label over body2-value pair with the em-dash empty state. */
function LabelValue({ label, value, align = 'left' }) {
  return (
    <>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.3 }}>
        {label}
      </Typography>
      <Typography
        component="div"
        variant="body2"
        sx={{
          color: 'text.primary',
          fontWeight: 500,
          lineHeight: 1.4,
          wordBreak: 'break-word',
          ...(align === 'right' ? { display: 'flex', justifyContent: 'flex-end' } : {}),
        }}
      >
        {value || value === 0 ? value : '—'}
      </Typography>
    </>
  );
}

/**
 * @param {object}     props
 * @param {React.node} props.label    - field label (small, muted)
 * @param {React.node} props.value    - field value (prominent)
 * @param {React.node} [props.action] - optional trailing control, vertically centered
 * @param {string}     [props.id]     - optional DOM id passed to the row
 * @param {boolean}    [props.dense]  - drop the row padding/min-height + action
 *   slot, rendering just the label/value cell for tight grid layouts
 * @param {'left'|'right'} [props.align] - dense mode only: right-justify the
 *   cell against its grid column (e.g. a signal indicator sharing a row with
 *   a left-aligned field). No effect outside dense mode.
 */
export default function DetailRow({ label, value, action, id, dense = false, align = 'left' }) {
  if (dense) {
    return (
      <Box id={id} sx={{ minWidth: 0, ...(align === 'right' ? { justifySelf: 'end', textAlign: 'right' } : {}) }}>
        <LabelValue label={label} value={value} align={align} />
      </Box>
    );
  }

  return (
    <Box
      id={id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 'var(--space-3)',
        py: 'var(--space-2)',
        minHeight: 44,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <LabelValue label={label} value={value} />
      </Box>
      {action ? <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{action}</Box> : null}
    </Box>
  );
}
